import { schema, t, type SchemaType } from "@colyseus/schema";
import type { PlayerSim } from "./sim.ts";
import { KO_RULES } from "./match.ts";

/**
 * Replicated fighter state. The first block mirrors `PlayerSim` field by field
 * (float32 on the wire, matching the simulation's own float32 rounding).
 */
export const PlayerState = schema(
  {
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    vx: t.float32().default(0),
    vy: t.float32().default(0),
    vz: t.float32().default(0),
    fx: t.float32().default(0),
    fz: t.float32().default(1),
    grounded: t.boolean().default(false),
    airTicks: t.uint8().default(0),
    dashTicks: t.uint8().default(0),
    dashCooldown: t.uint8().default(0),
    spawnIndex: t.uint8().default(0),
    action: t.uint8().default(0),
    actionTick: t.uint8().default(0),
    actionSerial: t.uint16().default(0),
    queued: t.uint8().default(0),
    queueTicks: t.uint8().default(0),
    hitstun: t.uint8().default(0),
    slamReady: t.boolean().default(false),
    cooldown1: t.uint16().default(0),
    cooldown2: t.uint16().default(0),
    carrying: t.uint8().default(0),
    slowTicks: t.uint8().default(0),
    slipTicks:t.uint8().default(0),
    spinTicks: t.uint8().default(0),
    anchorTicks:t.uint8().default(0),
    metamorphTicks:t.uint16().default(0),
    airBounceUsed: t.boolean().default(false),
    countered: t.boolean().default(false),
    knockedOut: t.boolean().default(false),
    matchFrozen: t.boolean().default(false),
    grabbed: t.boolean().default(false),
    hp: t.uint16().default(KO_RULES.maxHp),
    maxHp: t.uint16().default(KO_RULES.maxHp),
    respawnTicks: t.uint16().default(0),
    spawnShieldTicks: t.uint8().default(0),

    name: t.string().default(""),
    /** Index into the client colour palette. */
    color: t.uint8().default(0),
    connected: t.boolean().default(true),
    /** Lobby: has picked a champion in this room (bots always have); kept from one match to the next. */
    ready: t.boolean().default(false),
    characterId: t.string().default(""),
    /** `Team`: 0 in a free-for-all. Set by the server in the lobby. */
    team: t.uint8().default(0),
    /** A sparring bot run by the server (training), not a person. */
    bot: t.boolean().default(false),

    // Authoritative combat results: never read by the simulation, never predicted.
    /** Slam meter, 0–`MatchRules.slamMax`. */
    slam: t.uint8().default(0),
    score: t.uint16().default(0),
    /** Anti-chain protection after a Super Slam: further Super Slams pass through. */
    shielded: t.boolean().default(false),
    /** `Buff` from an object thrown by the crowd (0 = none). */
    buff: t.uint8().default(0),
    /** Charmed (Cleopatra's Super Slam): ticks left, and who: it walks to her and cannot attack. */
    charmTicks: t.uint8().default(0),
    charmer: t.string().default(""),
    /** Poisoned (Cleopatra's asp): ticks left of the venom. */
    poisonTicks: t.uint8().default(0),
  },
  "PlayerState",
);
export type PlayerState = SchemaType<typeof PlayerState>;

/** One row of the final ranking, frozen at the end so every client shows the same results. */
export const ResultEntry = schema(
  {
    sessionId: t.string().default(""),
    name: t.string().default(""),
    color: t.uint8().default(0),
    score: t.uint16().default(0),
    left: t.boolean().default(false),
    characterId: t.string().default(""),
    team: t.uint8().default(0),
    /** Arena KO: still standing at the end (the winner, or one of those sharing the draw). */
    standing: t.boolean().default(false),
  },
  "ResultEntry",
);
export type ResultEntry = SchemaType<typeof ResultEntry>;

/**
 * A projectile in flight (ranged abilities). Simulated by the server only;
 * the clients interpolate it like the other fighters.
 */
export const ProjectileState = schema(
  {
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    /** Direction of flight (unit vector), for drawing. */
    fx: t.float32().default(0),
    fz: t.float32().default(1),
    radius: t.float32().default(0.3),
    /** Session id of the shooter. */
    owner: t.string().default(""),
    /** A thrown object of the map: its kind, drawn instead of the energy ball ("" = an ability's shot). */
    prop: t.string().default(""),
    kind: t.string().default(""),
    /** Colour of the shooter (palette index). */
    color: t.uint8().default(0),
  },
  "ProjectileState",
);
export type ProjectileState = SchemaType<typeof ProjectileState>;

/**
 * An object thrown by the crowd (D38). While `fall` > 0 it is still flying in
 * from the stands towards (x, y, z); then it lies there until picked up.
 */
export const PickupState = schema(
  {
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    /** `PickupKind`. */
    kind: t.uint8().default(1),
    /** Ticks of flight left. */
    fall: t.uint8().default(0),
  },
  "PickupState",
);
export type PickupState = SchemaType<typeof PickupState>;

/** An object of the map (D39): where it is, how much it still takes, who carries it. */
export const PropState = schema(
  {
    /** Where it lies (dropped objects move). */
    x: t.float32().default(0),
    y: t.float32().default(0),
    z: t.float32().default(0),
    /** Hits left before it breaks (breakable objects). */
    hp: t.uint8().default(0),
    collapseTicks: t.uint16().default(0),
    /** `PropStatus`. */
    status: t.uint8().default(0),
    /** Session id of who carries it ("" = nobody). */
    carrier: t.string().default(""),
  },
  "PropState",
);
export type PropState = SchemaType<typeof PropState>;

/** Lobby choices of the host (`RoomSettings`). */
export const SettingsState = schema(
  {
    gameMode: t.uint8().default(0),
    maxPlayers: t.uint8().default(8),
    targetScore: t.uint8().default(10),
    durationSec: t.uint16().default(180),
    /** 0 = free-for-all, 1 = teams. */
    teams: t.uint8().default(0),
  },
  "SettingsState",
);
export type SettingsState = SchemaType<typeof SettingsState>;

export const BarricadeState = schema({
  x:t.float32().default(0), y:t.float32().default(0), z:t.float32().default(0),
  alongX:t.boolean().default(true), hp:t.uint8().default(6),
  ticks:t.uint16().default(600), owner:t.string().default(''),
}, 'BarricadeState');
export type BarricadeState = SchemaType<typeof BarricadeState>;

export const ZoneState = schema({
  x: t.float32().default(0), y: t.float32().default(0), z: t.float32().default(0),
  radius: t.float32().default(1.5), ticks: t.uint8().default(54), owner: t.string().default(""),
  kind: t.string().default("roots"), duration: t.uint8().default(54),
}, "ZoneState");
export type ZoneState = SchemaType<typeof ZoneState>;

/** Persistent telegraph: reconnecting clients see the same impending impact. */
export const BombState = schema({
  x:t.float32().default(0), y:t.float32().default(0), z:t.float32().default(0),
  owner:t.string().default(""), strike:t.uint32().default(0), lane:t.uint8().default(0),
  ticks:t.uint16().default(0), duration:t.uint16().default(0), radius:t.float32().default(2.2),
  flightSpeed:t.float32().default(12),
  flightX:t.float32().default(12), flightZ:t.float32().default(0),
}, "BombState");
export type BombState = SchemaType<typeof BombState>;

export const CloneState = schema({
  owner:t.string().default(''), ticks:t.uint16().default(240),
  actor:PlayerState,
}, 'CloneState');
export type CloneState = SchemaType<typeof CloneState>;

export const TerrainCellState=schema({hp:t.uint8().default(30),depth:t.uint8().default(0)},'TerrainCellState');
export type TerrainCellState=SchemaType<typeof TerrainCellState>;

/** Independently damageable servants; actor is not a player or a scoring target. */
export const CreatureState=schema({owner:t.string().default(''),kind:t.string().default('migo'),ticks:t.uint16().default(0),actor:PlayerState},'CreatureState');
export type CreatureState=SchemaType<typeof CreatureState>;
export const TentacleState=schema({owner:t.string().default(''),strike:t.uint32().default(0),x:t.float32().default(0),y:t.float32().default(0),z:t.float32().default(0),fx:t.float32().default(1),fz:t.float32().default(0),ticks:t.uint16().default(42),length:t.float32().default(4.2),width:t.float32().default(1.35)},'TentacleState');
export type TentacleState=SchemaType<typeof TentacleState>;

export const ArenaState = schema(
  {
    arenaId: t.string().default(""),
    players: t.map(PlayerState),
    /** Keyed by a per-room counter. */
    projectiles: t.map(ProjectileState),
    /** Objects of the map, keyed by their id in the arena definition. */
    props: t.map(PropState),
    terrain:t.map(TerrainCellState),
    zones: t.map(ZoneState),
    bombs: t.map(BombState),
    barricades: t.map(BarricadeState),
    clones: t.map(CloneState),
    creatures:t.map(CreatureState),
    tentacles:t.map(TentacleState),
    /** Objects thrown by the crowd, keyed by a per-room counter. */
    pickups: t.map(PickupState),
    /** `TrapPhase` of the arena's trap (lever and hatches). */
    trap: t.uint8().default(0),
    /** Applause meter, 0–`CROWD.max`. */
    crowd: t.uint8().default(0),

    /** Private room code to share (GDD §6.3); also the room id. */
    code: t.string().default(""),
    /** Session id of the host: settings, start, kick. Moves on if the host leaves. */
    hostId: t.string().default(""),
    settings: SettingsState,

    /** `MatchPhase`. */
    phase: t.string().default("lobby"),
    /** Seconds left in the current phase (countdown, match timer, results). */
    timeLeft: t.uint16().default(0),
    /** Increments at every new match. */
    matchId: t.uint16().default(0),
    /** Score that wins the current match (a player's in a free-for-all, a team's in a team match). */
    goal: t.uint16().default(0),
    /** Arena KO: time ran out with several standing, the minute of deathmatch is on (`timeLeft` counts it down). */
    overtime: t.boolean().default(false),
    teamScoreBlue: t.uint16().default(0),
    teamScoreRed: t.uint16().default(0),
    /** Set when a match ends: ranked rows, winner session id ("" = draw), `EndReason`. */
    results: t.array(ResultEntry),
    winner: t.string().default(""),
    /** Team match: winning `Team` (0 = draw, or a free-for-all). */
    winnerTeam: t.uint8().default(0),
    endReason: t.string().default(""),
  },
  "ArenaState",
);
export type ArenaState = SchemaType<typeof ArenaState>;

/** Copies simulation fields onto the replicated state, writing only changes. */
export function writeSimToState(sim: PlayerSim, state: PlayerState): void {
  if (state.x !== sim.x) state.x = sim.x;
  if (state.y !== sim.y) state.y = sim.y;
  if (state.z !== sim.z) state.z = sim.z;
  if (state.vx !== sim.vx) state.vx = sim.vx;
  if (state.vy !== sim.vy) state.vy = sim.vy;
  if (state.vz !== sim.vz) state.vz = sim.vz;
  if (state.fx !== sim.fx) state.fx = sim.fx;
  if (state.fz !== sim.fz) state.fz = sim.fz;
  if (state.grounded !== sim.grounded) state.grounded = sim.grounded;
  if (state.airTicks !== sim.airTicks) state.airTicks = sim.airTicks;
  if (state.dashTicks !== sim.dashTicks) state.dashTicks = sim.dashTicks;
  if (state.dashCooldown !== sim.dashCooldown) state.dashCooldown = sim.dashCooldown;
  if (state.spawnIndex !== sim.spawnIndex) state.spawnIndex = sim.spawnIndex;
  if (state.action !== sim.action) state.action = sim.action;
  if (state.actionTick !== sim.actionTick) state.actionTick = sim.actionTick;
  if (state.actionSerial !== sim.actionSerial) state.actionSerial = sim.actionSerial;
  if (state.queued !== sim.queued) state.queued = sim.queued;
  if (state.queueTicks !== sim.queueTicks) state.queueTicks = sim.queueTicks;
  if (state.hitstun !== sim.hitstun) state.hitstun = sim.hitstun;
  if (state.slamReady !== sim.slamReady) state.slamReady = sim.slamReady;
  if (state.cooldown1 !== sim.cooldown1) state.cooldown1 = sim.cooldown1;
  if (state.cooldown2 !== sim.cooldown2) state.cooldown2 = sim.cooldown2;
  if (state.carrying !== sim.carrying) state.carrying = sim.carrying;
  if (state.slowTicks !== sim.slowTicks) state.slowTicks = sim.slowTicks;
  if (state.slipTicks !== sim.slipTicks) state.slipTicks = sim.slipTicks;
  if (state.spinTicks !== sim.spinTicks) state.spinTicks = sim.spinTicks;
  if(state.anchorTicks!==sim.anchorTicks)state.anchorTicks=sim.anchorTicks;
  if (state.airBounceUsed !== sim.airBounceUsed) state.airBounceUsed = sim.airBounceUsed;
  if (state.countered !== sim.countered) state.countered = sim.countered;
  if (state.knockedOut !== sim.knockedOut) state.knockedOut = sim.knockedOut;
  if (state.matchFrozen !== sim.matchFrozen) state.matchFrozen = sim.matchFrozen;
  if (state.grabbed !== sim.grabbed) state.grabbed = sim.grabbed;
}
